import { useCallback, useState } from 'react';
import { useStepScormUploader } from '@/features/StepScormUploads';
import { useStepVideoUploader } from '@/features/StepVideoUploads';
import { useCreateLessonStepMutation } from '@/entities/Courses';
import type { SessionStepDraft } from './types';

type PatchStep = (clientId: string, patch: Partial<SessionStepDraft>) => void;

interface SaveStage {
  number: number;
  total: number;
}

const getStatus = (stage: SaveStage | null) =>
  stage ? `Saving step ${stage.number} of ${stage.total}...` : null;

// Subject of the "... uploading" notice: "A video is", "2 SCORM packages are", "3 files are"
export const describeBackgroundUploads = (videos: number, packages: number) => {
  const total = videos + packages;
  if (total === 0) return null;
  if (total === 1) return videos === 1 ? 'A video is' : 'A SCORM package is';
  if (packages === 0) return `${total} videos are`;
  if (videos === 0) return `${total} SCORM packages are`;
  return `${total} files are`;
};

export const getSaveErrorMessage = (error: unknown, fallback: string) => {
  const { status, data, message } = (error ?? {}) as {
    status?: number;
    data?: { message?: string; error?: { message?: string } };
    message?: string;
  };
  if (status === 409) {
    return 'A SCORM package is still being processed. Try again when processing is finished';
  }
  return data?.message || data?.error?.message || message || fallback;
};

/**
 * Creates the drafted steps of a new lesson one request at a time.
 * Each finished request is reported through `patchStep`, so a retry after a failure
 * continues from where it stopped instead of creating the steps again.
 * Videos and SCORM packages are not awaited: they go to the uploads managers and keep
 * uploading in the background after the modal closes.
 */
export const useSaveSessionSteps = () => {
  const [createLessonStep] = useCreateLessonStepMutation();
  const videoUploader = useStepVideoUploader();
  const scormUploader = useStepScormUploader();
  const [stage, setStage] = useState<SaveStage | null>(null);

  const saveSteps = useCallback(
    async (lessonId: number, drafts: SessionStepDraft[], patchStep: PatchStep, lessonTitle: string) => {
      let queuedVideos = 0;
      let queuedPackages = 0;
      try {
        for (const [index, draft] of drafts.entries()) {
          const number = index + 1;
          let stepId = draft.serverId;
          if (!stepId) {
            setStage({ number, total: drafts.length });
            const created = await createLessonStep({
              lessonId,
              body: {
                ...(draft.type ? { type: draft.type } : {}),
                title: draft.title.trim(),
                description: draft.description,
                content: draft.content,
                attachments: draft.attachments.length > 0 ? draft.attachments : undefined,
              },
            }).unwrap();
            stepId = created.data.step.id;
            patchStep(draft.clientId, { serverId: stepId });
          }

          if (draft.type === 'video' && draft.videoFile && !draft.videoUploaded) {
            videoUploader.start(stepId, draft.videoFile, { stepNumber: number, lessonTitle });
            patchStep(draft.clientId, { videoUploaded: true });
            queuedVideos += 1;
          }

          if (draft.type === 'scorm' && draft.scormFile && !draft.scormUploaded) {
            scormUploader.start(
              stepId,
              draft.scormFile,
              { stepNumber: number, lessonTitle },
              { graded: draft.scormGraded, passingScore: draft.scormPassingScore },
            );
            patchStep(draft.clientId, { scormUploaded: true });
            queuedPackages += 1;
          }
        }
      } finally {
        setStage(null);
      }
      return { queuedVideos, queuedPackages };
    },
    [createLessonStep, videoUploader, scormUploader],
  );

  return {
    saveSteps,
    isSaving: stage !== null,
    status: getStatus(stage),
  };
};
